import React from 'react';

import { FakeTrackingStatusProvider } from '../../../../tracking/components/functional/FakeTrackingStatusProvider';
import { TrackingStatusProvider } from '../../../../tracking/components/functional/TrackingStatusProvider';
import { QuizItem } from '../../../containers/student/QuizItem';
import { ResourceItem } from '../../../containers/student/ResourceItem';
import { SequencedFlowItem } from '../../../containers/student/SequencedFlowItem';
import { NonSequencedNavigationMenuItem } from './NonSequencedNavigationMenuItem';
import { ResourceFolderNavigationMenuItem } from './ResourceFolderNavigationMenuItem';
import { SubmenuNavigationMenuItem } from './SubmenuNavigationMenuItem';
import {
  ensureIsResourcesFolder,
  Item,
  ItemType,
  MenuItem,
  NonSequencedFlowItem,
  Quiz,
  Resource,
  SequencedFlowItem as SequencedFlowItemModel,
  TrackingStatus,
} from '@ael/core-api';
import { MenuItemLabel } from '@ael/shared-components-library';

interface NavigationMenuItemProps {
  isStudent: boolean;
  itemId: string;
  parentItemId: string | null;
  nestedLevel: number;
  getItemById: (itemId: string) => Item;
  isLinearModeOn: boolean;
}

export const NavigationMenuItem: React.FC<NavigationMenuItemProps> = React.memo(
  ({ isStudent, itemId, parentItemId, nestedLevel, getItemById, isLinearModeOn }) => {
    const item = getItemById(itemId);

    const TrackingStatusProviderComponent = isStudent ? TrackingStatusProvider : FakeTrackingStatusProvider;

    switch (item.type) {
      case ItemType.MenuItem:
        return (
          <SubmenuNavigationMenuItem
            item={item as MenuItem}
            isLinearModeOn={isLinearModeOn}
            parentItemId={parentItemId}
            nestedLevel={nestedLevel}
            getItemById={getItemById}
            TrackingStatusProvider={TrackingStatusProviderComponent}
            isStudent={isStudent}
          />
        );
      case ItemType.NonSequencedFlow:
        return (
          <NonSequencedNavigationMenuItem
            item={item as NonSequencedFlowItem}
            parentItemId={parentItemId}
            nestedLevel={nestedLevel}
            isLinearModeOn={isLinearModeOn}
            TrackingStatusProvider={TrackingStatusProviderComponent}
          />
        );
      case ItemType.SequencedFlow:
        return (
          <TrackingStatusProviderComponent
            itemId={item.id}
            render={(trackingStatus: TrackingStatus) => (
              <SequencedFlowItem
                item={item as SequencedFlowItemModel}
                key={item.id}
                trackingStatus={trackingStatus}
                parentItemId={parentItemId}
                nestedLevel={nestedLevel}
                isLinearModeOn={isLinearModeOn}
              >
                <MenuItemLabel>{item.title}</MenuItemLabel>
              </SequencedFlowItem>
            )}
          />
        );
      case ItemType.Quiz:
        return (
          <TrackingStatusProviderComponent
            itemId={item.id}
            render={(trackingStatus: TrackingStatus) => (
              <QuizItem
                item={item as Quiz}
                key={item.id}
                trackingStatus={trackingStatus}
                parentItemId={parentItemId}
                nestedLevel={nestedLevel}
                isLinearModeOn={isLinearModeOn}
              >
                <MenuItemLabel>{item.title}</MenuItemLabel>
              </QuizItem>
            )}
          />
        );
      case ItemType.ResourcesFolder:
        ensureIsResourcesFolder(item);
        return (
          <ResourceFolderNavigationMenuItem
            item={item}
            nestedLevel={nestedLevel}
            getItemById={getItemById}
            isLinearModeOn={isLinearModeOn}
            isStudent={isStudent}
          />
        );
      case ItemType.Resource:
        return (
          <ResourceItem item={item as Resource} key={item.id} isLinearModeOn={isLinearModeOn}>
            <MenuItemLabel>{item.title}</MenuItemLabel>
          </ResourceItem>
        );
      default:
        return null;
    }
  }
);
